<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { formatDistanceToNow } from 'date-fns';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';

const props = defineProps({
  activity: {
    type: Object,
    required: true,
  },
});

const { t } = useI18n();

const iconClass = computed(() => props.activity.icon_class || 'i-lucide-activity');
const colorClass = computed(() => props.activity.color_class || 'text-n-slate-11 bg-n-slate-9/10');

const timeAgo = computed(() => {
  if (!props.activity.occurred_at) return '';
  try {
    return formatDistanceToNow(new Date(props.activity.occurred_at), { addSuffix: true });
  } catch (e) {
    return '';
  }
});

const showMetadata = computed(() => {
  const typesWithMeta = ['call', 'meeting', 'stage_change', 'quote_sent', 'quote_accepted', 'quote_rejected'];
  return typesWithMeta.includes(props.activity.activity_type);
});
</script>

<template>
  <div class="flex gap-3 p-4 hover:bg-n-alpha-black2 transition-colors">
    <!-- Icon -->
    <div
      class="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center"
      :class="colorClass"
    >
      <span :class="iconClass" class="text-sm" />
    </div>

    <!-- Content -->
    <div class="flex-1 min-w-0">
      <!-- Header -->
      <div class="flex items-center gap-2 mb-1">
        <Avatar
          v-if="activity.user"
          :name="activity.user.name"
          :src="activity.user.avatar_url"
          :size="20"
          rounded-full
          hide-offline-status
        />
        <span v-if="activity.user" class="text-xs font-medium text-n-slate-12">
          {{ activity.user.name }}
        </span>
        <span class="text-xs text-n-slate-11">
          {{ timeAgo }}
        </span>
      </div>

      <!-- Content -->
      <p class="text-sm text-n-slate-12">
        {{ activity.content }}
      </p>

      <!-- Metadata -->
      <div v-if="showMetadata && activity.metadata" class="mt-2 text-xs text-n-slate-11">
        <!-- Call Duration -->
        <span v-if="activity.metadata.duration" class="flex items-center gap-1">
          <span class="i-lucide-clock" />
          {{ activity.metadata.duration }} {{ t('ACTIVITIES.MINUTES') }}
        </span>

        <!-- Stage Change -->
        <span v-if="activity.metadata.from_stage && activity.metadata.to_stage" class="flex items-center gap-1">
          <span class="i-lucide-git-branch" />
          {{ activity.metadata.from_stage }} → {{ activity.metadata.to_stage }}
        </span>

        <!-- Quote Status -->
        <span v-if="activity.metadata.status" class="flex items-center gap-1">
          <span class="i-lucide-file-text" />
          #{{ activity.metadata.quote_id }} - {{ activity.metadata.status }}
        </span>
      </div>
    </div>
  </div>
</template>
